/**
 * Translation runtime.
 *
 * Kept in `@yummy/shared` rather than the UI package because formatting helpers
 * in this package — durations, dates, opening hours — need the same translator,
 * and those have no React dependency.
 */

import { en, type MessageKey, type Messages } from './en';
import { zhHant } from './zh-Hant';

export type { MessageKey, Messages } from './en';

/** Locales the product ships. Adding one means adding a dictionary and a row here. */
export type Locale = 'en' | 'zh-Hant';

export interface LocaleInfo {
  code: Locale;
  /** Name in English, for logs and fallbacks. */
  label: string;
  /** Name in the language itself, which is what a language picker should show. */
  nativeLabel: string;
  /** BCP 47 tag handed to `Intl`. */
  intlTag: string;
}

export const LOCALES: LocaleInfo[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', intlTag: 'en' },
  { code: 'zh-Hant', label: 'Traditional Chinese', nativeLabel: '繁體中文', intlTag: 'zh-Hant' },
];

export const DEFAULT_LOCALE: Locale = 'en';

const DICTIONARIES: Record<Locale, Messages> = {
  en: en as Messages,
  'zh-Hant': zhHant,
};

/**
 * Narrows an arbitrary string to a supported locale.
 *
 * Accepts a full browser tag such as `zh-Hant-TW` or `zh-TW` and maps it to the
 * closest shipped locale, because `navigator.language` is rarely an exact match.
 *
 * @param value A language tag, or nullish.
 * @returns The matching locale, or null when nothing is close enough.
 */
export function matchLocale(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const tag = value.toLowerCase();

  for (const locale of LOCALES) {
    if (locale.code.toLowerCase() === tag) return locale.code;
  }

  // Any Chinese variant that is not explicitly Simplified maps to Traditional:
  // this product ships Traditional, and guessing Simplified would be wrong for
  // the users it is aimed at.
  if (tag.startsWith('zh')) {
    return tag.includes('hans') || tag.startsWith('zh-cn') || tag.startsWith('zh-sg')
      ? null
      : 'zh-Hant';
  }

  if (tag.startsWith('en')) return 'en';

  return null;
}

/**
 * The locale to use on first run, from the browser.
 *
 * @returns A shipped locale, defaulting to English.
 */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  return matchLocale(navigator.language) ?? matchLocale(navigator.languages?.[0]) ?? DEFAULT_LOCALE;
}

/**
 * Whether a value is a locale this build ships.
 *
 * @param value Candidate value, typically read from storage.
 * @returns True when the value is a known locale.
 */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && LOCALES.some((locale) => locale.code === value);
}

/** Parameters available to an interpolation slot. */
export type MessageParams = Record<string, string | number>;

/**
 * Replaces `{name}` slots in a template.
 *
 * A slot with no matching parameter is left visible rather than blanked, so a
 * wiring mistake shows up as `{count}` on screen instead of as a sentence that
 * silently reads wrong.
 *
 * @param template The message with slots.
 * @param params Values to substitute.
 * @returns The interpolated string.
 */
export function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match,
  );
}

type Leaf = string | { one: string; other: string };

function resolve(messages: Messages, path: string): Leaf | undefined {
  const segments = path.split('.');
  let cursor: unknown = messages;

  for (const segment of segments) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }

  if (typeof cursor === 'string') return cursor;
  if (cursor && typeof cursor === 'object' && 'one' in cursor && 'other' in cursor) {
    return cursor as { one: string; other: string };
  }
  return undefined;
}

/** A bound translation function. */
export interface Translator {
  (key: MessageKey, params?: MessageParams): string;
  /** The locale this translator resolves against. */
  locale: Locale;
}

/**
 * Builds a translator for one locale.
 *
 * Falls back to English for any key the active locale is missing, so a partially
 * translated dictionary degrades to readable text rather than to a raw key. The
 * type system prevents that at build time; this handles dictionaries assembled
 * at runtime.
 *
 * @param locale The active locale.
 * @returns A `t` function with its locale attached.
 */
export function createTranslator(locale: Locale): Translator {
  const primary = DICTIONARIES[locale] ?? DICTIONARIES[DEFAULT_LOCALE];
  const fallback = DICTIONARIES[DEFAULT_LOCALE];
  const plural = new Intl.PluralRules(LOCALES.find((l) => l.code === locale)?.intlTag ?? locale);

  const translate = ((key: MessageKey, params?: MessageParams): string => {
    const leaf = resolve(primary, key) ?? resolve(fallback, key);

    // A missing key means a dictionary and its type have drifted apart. Showing
    // the key makes the gap obvious without crashing the screen.
    if (leaf === undefined) return key;

    const template =
      typeof leaf === 'string'
        ? leaf
        : plural.select(Number(params?.count ?? 0)) === 'one'
          ? leaf.one
          : leaf.other;

    return interpolate(template, params);
  }) as Translator;

  translate.locale = locale;
  return translate;
}
